import React, { useState } from 'react';
import { useSurvey } from '../../../context/SurveyContext';
import { ArrowLeft, BarChart3, PieChart, FileText, Download, TrendingUp, MessageSquare, Wand2, Calculator } from 'lucide-react';
import SummaryTab from './SummaryTab';
import QuestionAnalysis from './QuestionAnalysis';
import IndividualResponses from './IndividualResponses';
import DescriptiveStatistics from '../Analytics/DescriptiveStatistics';
import StatisticalTests from '../Analytics/StatisticalTests';
import TextAnalysis from '../Analytics/TextAnalysis';
import ExportData from './ExportData';
import './ResultsDashboard.css';

const tabs = [
  { id: 'summary', label: 'Summary', icon: BarChart3 },
  { id: 'questions', label: 'Question Analysis', icon: PieChart },
  { id: 'responses', label: 'Individual Responses', icon: FileText },
  { id: 'descriptive', label: 'Descriptive Statistics', icon: Calculator },
  { id: 'statistics', label: 'Inferential Statistics', icon: TrendingUp },
  { id: 'text', label: 'Text Analysis', icon: MessageSquare },
  { id: 'export', label: 'Export Data', icon: Download }
];

const ResultsDashboard = ({ onBack }) => {
  const { currentSurvey, addSampleResponses } = useSurvey();
  const [activeTab, setActiveTab] = useState('summary');
  const [generating, setGenerating] = useState(false);

  if (!currentSurvey) {
    return (
      <div className="results-empty">
        <p>No survey selected</p>
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
      </div>
    );
  }

  const responseCount = currentSurvey.responses?.length || 0;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary':
        return <SummaryTab survey={currentSurvey} />;
      case 'questions':
        return <QuestionAnalysis survey={currentSurvey} />;
      case 'responses':
        return <IndividualResponses survey={currentSurvey} />;
      case 'descriptive':
        return <DescriptiveStatistics survey={currentSurvey} />;
      case 'statistics':
        return <StatisticalTests survey={currentSurvey} />;
      case 'text':
        return <TextAnalysis survey={currentSurvey} />;
      case 'export':
        return <ExportData survey={currentSurvey} />;
      default:
        return null;
    }
  };

  return (
    <div className="results-dashboard">
      <div className="results-header">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={18} />
          Back to Survey
        </button>
        <div className="results-title">
          <h1>{currentSurvey.title}</h1>
          <span className="response-count">{responseCount} response{responseCount !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="results-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`results-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="results-content">
        {responseCount === 0 && activeTab !== 'export' ? (
          <div className="no-responses">
            <BarChart3 size={48} />
            <h3>No Responses Yet</h3>
            <p>Share your survey to start collecting responses. Results will appear here once people respond.</p>
            {currentSurvey.questions.length > 0 && (
              <button 
                className="btn-generate-sample"
                onClick={() => {
                  setGenerating(true);
                  setTimeout(() => {
                    addSampleResponses(currentSurvey.id, 400);
                    setGenerating(false);
                  }, 100);
                }}
                disabled={generating}
              >
                <Wand2 size={18} />
                {generating ? 'Generating...' : 'Generate 400 Sample Responses'}
              </button>
            )}
          </div>
        ) : (
          renderTabContent()
        )}
      </div>
    </div>
  );
};

export default ResultsDashboard;
